<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';

const emit = defineEmits(['error']);
const hasError = ref(false);
const showGallery = ref(false);
const blobUrl = ref(null);
const isLoading = ref(true);
const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const handleError = () => {
  hasError.value = true;
  emit('error');
};

const attachment = computed(() => {
  return attachments.value[0];
});

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});

// Fetch video with headers to bypass ngrok warning page in development
const loadVideo = async () => {
  const url = attachment.value?.dataUrl;
  if (!url) {
    isLoading.value = false;
    return;
  }

  try {
    isLoading.value = true;
    const response = await fetch(url, {
      headers: { 'ngrok-skip-browser-warning': 'true' },
    });

    if (!response.ok) {
      throw new Error('Failed to load video');
    }

    const blob = await response.blob();
    blobUrl.value = URL.createObjectURL(blob);
  } catch {
    hasError.value = true;
    emit('error');
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadVideo);
watch(() => attachment.value?.dataUrl, loadVideo);
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="video"
    @click="showGallery = true"
  >
    <div class="relative group rounded-lg overflow-hidden">
      <div
        v-if="isReel"
        class="absolute p-2 flex items-start justify-end right-0 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <div
        v-if="isLoading"
        class="flex items-center justify-center w-48 h-36 bg-n-slate-3 rounded-lg"
      >
        <Icon icon="i-lucide-loader-2" class="text-n-slate-11 animate-spin" />
      </div>
      <div
        v-else-if="hasError"
        class="flex items-center justify-center w-48 h-36 bg-n-slate-3 rounded-lg"
      >
        <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      </div>
      <video
        v-else
        controls
        class="rounded-lg skip-context-menu"
        :src="blobUrl"
        :class="{
          'max-w-48': isReel,
          'max-w-full': !isReel,
        }"
        @click.stop
        @error="handleError"
      />
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="onError"
    @close="() => (showGallery = false)"
  />
</template>
